import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { trainingLocationsAPI } from '../services/trainingLocations'
import { formatPlaces } from '../utils/exercisePlaces'
import { workoutPlannerAPI, equipmentAPI } from '../services/api'
import ExerciseFormModal from '../components/exercises/ExerciseFormModal'
import { MUSCLE_GROUPS, DIFFICULTY_LEVELS } from '../components/exercises/exerciseOptions'
import { useAuth } from '../hooks/useAuth'
import useTranslationCache from '../hooks/useTranslationCache'
import { resolveTemplateTargets, metricFieldLabel } from '../components/workouts/workoutHelpers'
import {
  FireIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  VideoCameraIcon,
  InformationCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { contentLanguage } from '../i18n/supported'

export default function ExerciseLibrary() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || false
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')
  const [selectedEquipment, setSelectedEquipment] = useState('')
  const [selectedUsageFilter, setSelectedUsageFilter] = useState('') // '', 'in_use', 'not_in_use'
  // VH-136. Deliberately starts empty (= all locations) and is NOT persisted:
  // VH-130 found that a remembered filter reads as data loss.
  const [selectedLocation, setSelectedLocation] = useState('')
  const [selectedExercise, setSelectedExercise] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingExercise, setEditingExercise] = useState(null)

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch exercises (using debounced search term to avoid excessive API calls)
  const { data: exercisesData, isLoading } = useQuery({
    queryKey: ['exercises', debouncedSearchTerm, selectedMuscleGroup, selectedDifficulty, selectedEquipment, selectedLocation],
    queryFn: () => workoutPlannerAPI.getExercises({
      search_term: debouncedSearchTerm || undefined,
      muscle_group: selectedMuscleGroup || undefined,
      difficulty_level: selectedDifficulty || undefined,
      equipment_id: selectedEquipment || undefined,
      location_id: selectedLocation || undefined
    })
  })

  // VH-136: the places this user trains, for the location filter.
  const { data: locationsData } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data)
  })
  const locations = locationsData?.locations ?? locationsData ?? []

  // Fetch equipment for filter and form (use limit: 100 to get all equipment, sorted alphabetically)
  const { data: equipmentData } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => equipmentAPI.getAll({ limit: 100, sort_by: 'name_en' })
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: workoutPlannerAPI.deleteExercise,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] })
    }
  })

  const handleDelete = (exercise, e) => {
    e.stopPropagation()

    const templateCount = exercise.usage?.template_count || 0
    const workoutCount = exercise.usage?.workout_count || 0

    // Check if exercise is assigned to modules (most important)
    if (templateCount > 0) {
      const usageInfo = `${t('exercises.inModuleWarning')}\n\n` +
        `${t('templates.title')}: ${templateCount}`

      if (!confirm(usageInfo + '\n\n' + t('exercises.confirmDeleteInModule'))) {
        return
      }
      deleteMutation.mutate({ id: exercise.id, force: true })
    }
    // Check if exercise has workout history
    else if (workoutCount > 0) {
      const usageInfo = `${t('exercises.hasHistoryWarning')}\n\n` +
        `${t('exercises.workoutHistory')}: ${workoutCount}`

      if (!confirm(usageInfo + '\n\n' + t('exercises.confirmDeleteWithHistory'))) {
        return
      }
      deleteMutation.mutate({ id: exercise.id, force: true })
    }
    // No usage - simple delete
    else {
      if (confirm(t('exercises.confirmDelete'))) {
        deleteMutation.mutate({ id: exercise.id, force: false })
      }
    }
  }

  const handleEdit = (exercise, e) => {
    e.stopPropagation()
    setEditingExercise(exercise)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingExercise(null)
    setShowModal(true)
  }

  const allExercises = exercisesData?.data?.exercises || []
  const equipment = equipmentData?.data?.equipment || []

  // Filter exercises by usage status (client-side filter since usage info is already included)
  const exercises = allExercises.filter(exercise => {
    if (!selectedUsageFilter) return true
    const isInUse = exercise.usage?.template_count > 0
    if (selectedUsageFilter === 'in_use') return isInUse
    if (selectedUsageFilter === 'not_in_use') return !isInUse
    return true
  })

  // VH-36: Translation caches via shared hook
  const { getTranslation: getExerciseTranslation } = useTranslationCache({
    items: exercises,
    prefix: 'exercise',
    fetchTranslation: (id, lang) => workoutPlannerAPI.translateExercise(id, lang),
  })
  const { getTranslation: getEquipmentTranslation } = useTranslationCache({
    items: equipment,
    prefix: 'equipment',
    fetchTranslation: (id, lang) => equipmentAPI.translate(id, lang),
  })

  // Helper to get translated equipment name
  const getEquipmentName = useCallback((eq) => {
    if (!eq) return ''
    return getEquipmentTranslation(eq.id, 'name') || eq.name_en || eq.name || ''
  }, [getEquipmentTranslation])

  // VH-139: equipment is location-owned (migration 074), so the "same" Leg
  // Press at two gyms is two rows — by design. The picker showed only the name,
  // which made them look like duplicated data and gave no way to tell which one
  // you were choosing. The location is what distinguishes them, so it belongs
  // in the label rather than only on the equipment page.
  const getEquipmentLabel = useCallback((eq) => {
    const name = getEquipmentName(eq)
    const place = eq?.location_name
    return place ? `${name} — ${place}` : name
  }, [getEquipmentName])

  const getTranslatedValue = useCallback((exercise, field) => {
    if (!exercise) return ''
    return getExerciseTranslation(exercise.id, field) || exercise[`${field}_en`] || exercise[field] || ''
  }, [getExerciseTranslation])

  const getDifficultyColor = (difficulty) => {
    const level = DIFFICULTY_LEVELS.find(d => d.value === difficulty.toLowerCase())
    return level?.color || 'gray'
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('exercises.title')}</h1>
            <p className="mt-2 text-gray-600">
              {t('exercises.subtitleWithCount', { count: exercises.length })}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={handleAdd}
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('exercises.addExercise')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('exercises.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
            <select
              value={selectedMuscleGroup}
              onChange={(e) => setSelectedMuscleGroup(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('exercises.allMuscleGroups')}</option>
              {MUSCLE_GROUPS.map((group) => (
                <option key={group} value={group}>
                  {t(`exercises.muscleGroups.${group}`)}
                </option>
              ))}
            </select>

            <select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('exercises.allDifficultyLevels')}</option>
              {DIFFICULTY_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {t(`exercises.difficulty.${level.value}`)}
                </option>
              ))}
            </select>

            <select
              value={selectedEquipment}
              onChange={(e) => setSelectedEquipment(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('exercises.allEquipment')}</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {getEquipmentLabel(eq)}
                </option>
              ))}
            </select>

            {/* VH-136: where can I actually do this? Hidden rather than
                disabled when the user has no locations — a disabled control
                invites a question the app cannot answer yet. */}
            {locations.length > 0 && (
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                data-testid="exercise-location-filter"
                className="focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">{t('exercises.allLocations')}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            )}

            <select
              value={selectedUsageFilter}
              onChange={(e) => setSelectedUsageFilter(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">{t('common.all')}</option>
              <option value="in_use">{t('exercises.inModule')}</option>
              <option value="not_in_use">{t('exercises.notInModule')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Exercise Grid */}
      {exercises.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {exercises.map((exercise) => (
            <div
              key={exercise.id}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedExercise(exercise)}
            >
              <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {getTranslatedValue(exercise, 'name')}
                    </h3>
                    {exercise.usage?.template_count > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                        <LinkIcon className="h-3 w-3 mr-1" />
                        {t('exercises.inModule')} ({exercise.usage.template_count})
                      </span>
                    )}
                  </div>
                  <FireIcon className="h-6 w-6 text-primary-600 flex-shrink-0 ml-2" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <span className="font-medium text-gray-700 w-24">{t('exercises.muscle')}:</span>
                    <span className="text-gray-600 capitalize">
                      {t(`exercises.muscleGroups.${exercise.muscle_group}`, { defaultValue: exercise.muscle_group.replace(/_/g, ' ') })}
                    </span>
                  </div>

                  <div className="flex items-center text-sm">
                    <span className="font-medium text-gray-700 w-24">{t('exercises.difficulty.label')}:</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${getDifficultyColor(exercise.difficulty_level)}-100 text-${getDifficultyColor(exercise.difficulty_level)}-800`}>
                      {t(`exercises.difficulty.${exercise.difficulty_level.toLowerCase()}`)}
                    </span>
                  </div>

                  {exercise.equipment && (
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-700 w-24">{t('nav.equipment')}:</span>
                      <span className="text-gray-600">{getEquipmentName(exercise.equipment)}</span>
                    </div>
                  )}

                  {/* VH-136: where this can actually be done. All three states
                      are shown explicitly — somewhere, anywhere, nowhere —
                      because an empty space reads as "not loaded" rather than
                      as an answer. */}
                  <div className="flex items-start text-sm" data-testid="exercise-locations">
                    <span className="font-medium text-gray-700 w-24 shrink-0">
                      {t('exercises.whereLabel')}:
                    </span>
                    <span className="text-gray-600">
                      {exercise.location_independent
                        ? t('exercises.anywhere')
                        : formatPlaces(exercise.available_locations, t)}
                    </span>
                  </div>

                  <div className="flex items-center text-sm">
                    <span className="font-medium text-gray-700 w-24">{t('exercises.default')}:</span>
                    <span className="text-gray-600">
                      {exercise.default_training_sets} {t('exercises.sets')}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between pt-3 border-t border-gray-200">
                  <button className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
                    <InformationCircleIcon className="h-4 w-4 mr-1" />
                    {t('exercises.viewDetails')}
                  </button>
                  <div className="flex items-center space-x-2">
                    {/* VH-125: the badge must reflect the video the athlete will
                        actually get, which may be inherited from the equipment. */}
                    {exercise.effective_video_url && (
                      <VideoCameraIcon className="h-5 w-5 text-gray-400" title={t('exercises.videoAvailable')} />
                    )}
                    {isAdmin && (
                      <>
                        <button
                          onClick={(e) => handleEdit(exercise, e)}
                          className="text-gray-400 hover:text-primary-600"
                          title={t('common.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleDelete(exercise, e)}
                          className={`${exercise.usage?.template_count > 0 ? 'text-blue-400 hover:text-blue-600' : 'text-gray-400 hover:text-red-600'}`}
                          title={exercise.usage?.template_count > 0 ? t('exercises.inModuleDeleteWarning') : t('common.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FireIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('exercises.noExercises')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedMuscleGroup || selectedDifficulty
              ? t('exercises.adjustFilters')
              : t('exercises.noExercisesHint')}
          </p>
          {isAdmin && !searchTerm && !selectedMuscleGroup && !selectedDifficulty && (
            <div className="mt-6">
              <button onClick={handleAdd} className="btn-primary">
                <PlusIcon className="h-5 w-5 mr-2" />
                {t('exercises.addExercise')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Exercise Detail Modal */}
      {selectedExercise && (
        <ExerciseDetailModal
          exercise={selectedExercise}
          getTranslatedValue={getTranslatedValue}
          getEquipmentName={getEquipmentName}
          getEquipmentLabel={getEquipmentLabel}
          onClose={() => setSelectedExercise(null)}
          onEdit={(ex) => {
            // Close the detail view before opening the editor: two stacked
            // modals would trap focus in the one underneath.
            setSelectedExercise(null)
            setEditingExercise(ex)
            setShowModal(true)
          }}
        />
      )}

      {/* Exercise Form Modal */}
      {showModal && (
        <ExerciseFormModal
          exercise={editingExercise}
          equipment={equipment}
          getEquipmentName={getEquipmentName}
          getEquipmentLabel={getEquipmentLabel}
          onClose={() => {
            setShowModal(false)
            setEditingExercise(null)
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['exercises'] })
            setShowModal(false)
            setEditingExercise(null)
          }}
        />
      )}
    </div>
  )
}

// Exercise Detail Modal
function ExerciseDetailModal({ exercise, getTranslatedValue: parentGetTranslatedValue, getEquipmentName, onClose, onEdit }) {
  const { t, i18n } = useTranslation()
  const currentLanguage = contentLanguage(i18n?.language)

  // Wrap parent's two-arg helper to single-arg for convenience within modal
  const getTranslatedValue = (field) => parentGetTranslatedValue(exercise, field)

  // VH-149: what this exercise actually measures. Shared with the template
  // table so the two screens cannot drift apart again.
  const detailTargets = resolveTemplateTargets(exercise, {
    warmup_sets: exercise.default_warmup_sets,
    training_sets: exercise.default_training_sets,
  })

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">{getTranslatedValue('name')}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.muscleGroup')}</h4>
              <p className="text-gray-900 capitalize">{t(`exercises.muscleGroups.${exercise.muscle_group}`, { defaultValue: exercise.muscle_group.replace(/_/g, ' ') })}</p>
              {/* VH-125: advisory only. The exercise owns its muscle group; the
                  equipment's list is a broader claim about what the tool can
                  train, so a disagreement points at either record. */}
              {exercise.equipment_muscle_check === 'mismatch' && exercise.equipment && (
                <p className="mt-1 text-xs text-amber-700">
                  {t('exercises.muscleCheckMismatch', {
                    equipment: getEquipmentName(exercise.equipment),
                    muscle: t(`exercises.muscleGroups.${exercise.muscle_group}`),
                    defaultValue:
                      '{{equipment}} is not recorded as training {{muscle}} — check the equipment, or the exercise.',
                  })}
                </p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.difficultyLevel')}</h4>
              <p className="text-gray-900 capitalize">{t(`exercises.difficulty.${exercise.difficulty_level.toLowerCase()}`)}</p>
            </div>
          </div>

          {exercise.equipment && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.requiredEquipment')}</h4>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{getEquipmentName(exercise.equipment)}</p>
                <p className="text-sm text-gray-600 mt-1">{exercise.equipment.equipment_type}</p>
              </div>
            </div>
          )}

          {/* VH-149: the third surface to hardcode set parameters for every
              exercise. A hike does not have warmup sets — it has a duration, a
              distance, an intensity — and this panel showed "Training Sets 5 /
              Warmup Sets 0" for one, which is not merely useless but wrong.
              Same rule as the template table (VH-144) and the capture sheet
              (VH-145): what the exercise is configured to measure wins. */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              {detailTargets.kind === 'metrics'
                ? t('exercises.modal.measuredValues', { defaultValue: 'Measured Values' })
                : t('exercises.modal.recommendedParams')}
            </h4>

            {detailTargets.kind === 'metrics' ? (
              <div className="grid grid-cols-3 gap-4 p-4 bg-primary-50 rounded-lg">
                {detailTargets.fields.map((field) => (
                  <div key={field.key}>
                    <p className="text-sm text-gray-600">
                      {metricFieldLabel(field, currentLanguage)}
                    </p>
                    <p className="text-2xl font-bold text-primary-600">
                      {field.unit || '—'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4 p-4 bg-primary-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">{t('exercises.modal.trainingSets')}</p>
                  <p className="text-2xl font-bold text-primary-600">{exercise.default_training_sets}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('exercises.modal.warmupSets')}</p>
                  <p className="text-2xl font-bold text-primary-600">{exercise.default_warmup_sets}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('exercises.modal.restTime')}</p>
                  <p className="text-2xl font-bold text-primary-600">{exercise.default_rest_seconds}s</p>
                </div>
              </div>
            )}
          </div>

          {getTranslatedValue('instructions') && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.instructions')}</h4>
              <p className="text-gray-700">{getTranslatedValue('instructions')}</p>
            </div>
          )}

          {/* VH-125: the equipment is the source of record for videos. An
              exercise with none of its own inherits it, and its own value is an
              override — so label which one the athlete is looking at. */}
          {exercise.effective_video_url && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.videoInstruction')}</h4>
              <a
                href={exercise.effective_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 hover:text-primary-700"
              >
                <VideoCameraIcon className="h-5 w-5 mr-2" />
                {t('exercises.modal.watchVideoTutorial')}
              </a>
              <p className="mt-1 text-xs text-gray-400">
                {exercise.video_source === 'equipment'
                  ? t('exercises.videoInherited', {
                      equipment: getEquipmentName(exercise.equipment),
                      defaultValue: 'inherited from {{equipment}}',
                    })
                  : t('exercises.videoOwn', 'own video for this exercise')}
              </p>
            </div>
          )}

          {/* VH-125 AC: an explicit no-video state, rather than the section
              silently vanishing — an exercise with no equipment, or whose
              equipment has no video, has nothing to inherit. */}
          {!exercise.effective_video_url && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('exercises.modal.videoInstruction')}</h4>
              <p className="text-sm text-gray-400">
                {t('exercises.noVideo', 'No video for this exercise or its equipment')}
              </p>
            </div>
          )}
        </div>

        {/* VH-151: "I cannot even edit it". This modal was read-only, so
            reaching the metrics it displays meant closing it, finding the row
            again and using the pencil. The full editor — including the
            custom-metrics fields — already exists in ExerciseFormModal, so this
            hands off to it rather than growing a second form that would drift. */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">
            {t('common.close')}
          </button>
          {onEdit && (
            <button onClick={() => onEdit(exercise)} className="btn-primary">
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
